import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

let _isPrinting = false;

function _printViaIframe(blobUrl) {
  if (_isPrinting) return;
  _isPrinting = true;

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
  document.body.appendChild(iframe);

  const cleanup = () => {
    _isPrinting = false;
    try { document.body.removeChild(iframe); } catch {}
    URL.revokeObjectURL(blobUrl);
  };

  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch {
        window.open(blobUrl, '_blank');
      }
      setTimeout(cleanup, 1000);
    }, 500);
  };

  iframe.onerror = () => {
    window.open(blobUrl, '_blank');
    cleanup();
  };

  iframe.src = blobUrl;
}

export function printPdfBlob(blob) {
  const pdfBlob = blob.type === 'application/pdf'
    ? blob
    : new Blob([blob], { type: 'application/pdf' });
  const url = URL.createObjectURL(pdfBlob);
  _printViaIframe(url);
}

export async function printFromUrl(url) {
  if (_isPrinting) return;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const pdfBlob = blob.type === 'application/pdf'
      ? blob
      : new Blob([blob], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(pdfBlob);
    _printViaIframe(blobUrl);
  } catch {
    window.open(url, '_blank');
  }
}

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 10;
const CONTENT_WIDTH_MM = A4_WIDTH_MM - MARGIN_MM * 2;
const CONTENT_HEIGHT_MM = A4_HEIGHT_MM - MARGIN_MM * 2;

async function captureElement(el) {
  const canvas = await html2canvas(el, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
  });
  if (!canvas.width || !canvas.height) return null;
  return canvas;
}

function addCanvasToPdf(pdf, canvas, yMm) {
  const ratio = CONTENT_WIDTH_MM / canvas.width;
  const hMm = canvas.height * ratio;
  const imgData = canvas.toDataURL('image/png');
  pdf.addImage(imgData, 'PNG', MARGIN_MM, yMm, CONTENT_WIDTH_MM, hMm);
  return hMm;
}

export async function generateStudentPdf(element, studentName, options = {}) {
  const sections = element.querySelectorAll('[data-pdf-section]');

  if (!sections.length) {
    const canvas = await captureElement(element);
    if (!canvas) throw new Error('캡처된 이미지가 비어 있습니다.');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const ratio = CONTENT_WIDTH_MM / canvas.width;
    const imgH = canvas.height * ratio;

    if (imgH <= CONTENT_HEIGHT_MM) {
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', MARGIN_MM, MARGIN_MM, CONTENT_WIDTH_MM, imgH);
    } else {
      const pageHPx = CONTENT_HEIGHT_MM / ratio;
      let rem = canvas.height, srcY = 0, pg = 0;
      while (rem > 0 && pg < 20) {
        if (pg > 0) pdf.addPage();
        const slice = Math.min(pageHPx, rem);
        if (slice <= 0) break;
        const pc = document.createElement('canvas');
        pc.width = canvas.width; pc.height = slice;
        const ctx = pc.getContext('2d');
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, pc.width, pc.height);
        ctx.drawImage(canvas, 0, srcY, canvas.width, slice, 0, 0, canvas.width, slice);
        pdf.addImage(pc.toDataURL('image/png'), 'PNG', MARGIN_MM, MARGIN_MM, CONTENT_WIDTH_MM, slice * ratio);
        srcY += slice; rem -= slice; pg++;
      }
    }

    const filename = options.filename || `채점결과_${studentName || '학생'}.pdf`;
    return options.returnBlob ? pdf.output('blob') : pdf.save(filename);
  }

  const canvases = [];
  for (const sec of sections) {
    const c = await captureElement(sec);
    if (c) canvases.push(c);
  }
  if (!canvases.length) throw new Error('캡처된 이미지가 비어 있습니다.');

  const pdf = new jsPDF('p', 'mm', 'a4');
  let curY = MARGIN_MM;
  let pageStarted = true;

  for (const canvas of canvases) {
    const ratio = CONTENT_WIDTH_MM / canvas.width;
    const hMm = canvas.height * ratio;
    const remaining = CONTENT_HEIGHT_MM - (curY - MARGIN_MM);

    if (hMm > remaining && curY > MARGIN_MM + 2) {
      pdf.addPage();
      curY = MARGIN_MM;
      pageStarted = true;
    }

    if (hMm <= CONTENT_HEIGHT_MM) {
      addCanvasToPdf(pdf, canvas, curY);
      curY += hMm + 2;
    } else {
      let rem = canvas.height, srcY = 0;
      while (rem > 0) {
        if (!pageStarted) { pdf.addPage(); curY = MARGIN_MM; }
        const avail = (CONTENT_HEIGHT_MM - (curY - MARGIN_MM)) / ratio;
        const slice = Math.min(avail, rem);
        if (slice <= 0) break;
        const pc = document.createElement('canvas');
        pc.width = canvas.width; pc.height = slice;
        const ctx = pc.getContext('2d');
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, pc.width, pc.height);
        ctx.drawImage(canvas, 0, srcY, canvas.width, slice, 0, 0, canvas.width, slice);
        const sliceMm = slice * ratio;
        pdf.addImage(pc.toDataURL('image/png'), 'PNG', MARGIN_MM, curY, CONTENT_WIDTH_MM, sliceMm);
        curY += sliceMm;
        srcY += slice; rem -= slice;
        pageStarted = false;
      }
      curY += 2;
    }
    pageStarted = false;
  }

  const filename = options.filename || `채점결과_${studentName || '학생'}.pdf`;
  return options.returnBlob ? pdf.output('blob') : pdf.save(filename);
}

export async function generateBulkSinglePdf(elements, studentNames, options = {}) {
  if (!elements.length) throw new Error('캡처할 요소가 없습니다.');

  const pdf = new jsPDF('p', 'mm', 'a4');
  let isFirstStudent = true;

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    const sections = el.querySelectorAll('[data-pdf-section]');

    if (!isFirstStudent) pdf.addPage();
    isFirstStudent = false;

    if (!sections.length) {
      const canvas = await captureElement(el);
      if (!canvas) continue;
      const ratio = CONTENT_WIDTH_MM / canvas.width;
      const imgH = canvas.height * ratio;

      if (imgH <= CONTENT_HEIGHT_MM) {
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', MARGIN_MM, MARGIN_MM, CONTENT_WIDTH_MM, imgH);
      } else {
        const pageHPx = CONTENT_HEIGHT_MM / ratio;
        let rem = canvas.height, srcY = 0, pg = 0;
        while (rem > 0 && pg < 20) {
          if (pg > 0) pdf.addPage();
          const slice = Math.min(pageHPx, rem);
          if (slice <= 0) break;
          const pc = document.createElement('canvas');
          pc.width = canvas.width; pc.height = slice;
          const ctx = pc.getContext('2d');
          ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, pc.width, pc.height);
          ctx.drawImage(canvas, 0, srcY, canvas.width, slice, 0, 0, canvas.width, slice);
          pdf.addImage(pc.toDataURL('image/png'), 'PNG', MARGIN_MM, MARGIN_MM, CONTENT_WIDTH_MM, slice * ratio);
          srcY += slice; rem -= slice; pg++;
        }
      }
      continue;
    }

    const canvases = [];
    for (const sec of sections) {
      const c = await captureElement(sec);
      if (c) canvases.push(c);
    }

    let curY = MARGIN_MM;
    let pageStarted = true;

    for (const canvas of canvases) {
      const ratio = CONTENT_WIDTH_MM / canvas.width;
      const hMm = canvas.height * ratio;
      const remaining = CONTENT_HEIGHT_MM - (curY - MARGIN_MM);

      if (hMm > remaining && curY > MARGIN_MM + 2) {
        pdf.addPage();
        curY = MARGIN_MM;
        pageStarted = true;
      }

      if (hMm <= CONTENT_HEIGHT_MM) {
        addCanvasToPdf(pdf, canvas, curY);
        curY += hMm + 2;
      } else {
        let rem = canvas.height, srcY = 0;
        while (rem > 0) {
          if (!pageStarted) { pdf.addPage(); curY = MARGIN_MM; }
          const avail = (CONTENT_HEIGHT_MM - (curY - MARGIN_MM)) / ratio;
          const slice = Math.min(avail, rem);
          if (slice <= 0) break;
          const pc = document.createElement('canvas');
          pc.width = canvas.width; pc.height = slice;
          const ctx = pc.getContext('2d');
          ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, pc.width, pc.height);
          ctx.drawImage(canvas, 0, srcY, canvas.width, slice, 0, 0, canvas.width, slice);
          const sliceMm = slice * ratio;
          pdf.addImage(pc.toDataURL('image/png'), 'PNG', MARGIN_MM, curY, CONTENT_WIDTH_MM, sliceMm);
          curY += sliceMm;
          srcY += slice; rem -= slice;
          pageStarted = false;
        }
        curY += 2;
      }
      pageStarted = false;
    }
  }

  const filename = options.filename || '채점결과_전체.pdf';
  return options.returnBlob ? pdf.output('blob') : pdf.save(filename);
}

export async function generateBulkPdfs(elements, studentNames, options = {}) {
  const blobs = [];
  for (let i = 0; i < elements.length; i++) {
    const blob = await generateStudentPdf(elements[i], studentNames[i], { returnBlob: true });
    blobs.push({ blob, name: `채점결과_${studentNames[i] || `학생${i + 1}`}.pdf` });
  }

  if (blobs.length === 1) {
    const url = URL.createObjectURL(blobs[0].blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = blobs[0].name;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }

  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  blobs.forEach(({ blob, name }) => zip.file(name, blob));
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(zipBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = options.zipFilename || '채점결과_전체.zip';
  a.click();
  URL.revokeObjectURL(url);
}
